/**
 * Sync configuration service
 * Manages user sync preferences and settings
 */
import type { SyncConfiguration } from '../types/sync';

import { Preferences } from "@capacitor/preferences";

/**
 * Default sync configuration
 */
const DEFAULT_SYNC_CONFIG: SyncConfiguration = {
  id: 'config',
  enabled: true,
  dataTypes: {
    diaryEntries: true,
    userFoods: true,
    recipes: true,
    goals: true,
    weightEntries: true,
    meals: true,
  },
  autoSync: {
    enabled: true,
    intervalMinutes: 5,
    onlyOnWifi: false,
  },
  updatedAt: new Date().toISOString(),
};

const normalizeSyncConfiguration = (config: SyncConfiguration): SyncConfiguration => ({
  ...DEFAULT_SYNC_CONFIG,
  ...config,
  id: 'config',
  // Sync is intentionally all-or-nothing. Keep the legacy shape for stored
  // configuration compatibility while ensuring every collection participates.
  dataTypes: { ...DEFAULT_SYNC_CONFIG.dataTypes },
  autoSync: {
    ...DEFAULT_SYNC_CONFIG.autoSync,
    ...(config.autoSync ?? {}),
  },
});

/**
 * Sync configuration service
 */
export class SyncConfigurationService {
  private readonly PREFS_KEY = 'macrotracker:sync-configuration';
  private currentConfig: SyncConfiguration | null = null;

  /**
   * Load sync configuration
   */
  async loadConfiguration(): Promise<SyncConfiguration> {
    try {
      const { value } = await Preferences.get({ key: this.PREFS_KEY });
      if (value) {
        const stored = JSON.parse(value) as SyncConfiguration;
        const normalized = normalizeSyncConfiguration(stored);
        this.currentConfig = normalized;
        if (JSON.stringify(stored) !== JSON.stringify(normalized)) {
          await this.saveConfiguration(normalized);
        }
        return normalized;
      }
    } catch (error) {
      console.error('Failed to load sync configuration:', error);
    }

    // Return default configuration if none stored
    this.currentConfig = { ...DEFAULT_SYNC_CONFIG };
    await this.saveConfiguration(this.currentConfig);
    return this.currentConfig;
  }

  /**
   * Save sync configuration
   */
  async saveConfiguration(config: SyncConfiguration): Promise<void> {
    try {
      const updatedConfig = normalizeSyncConfiguration({ ...config, updatedAt: new Date().toISOString() });
      await Preferences.set({ key: this.PREFS_KEY, value: JSON.stringify({ ...updatedConfig, id: 'config' }) });
      this.currentConfig = updatedConfig;
    } catch (error) {
      console.error('Failed to save sync configuration:', error);
      throw error;
    }
  }

  /**
   * Get current configuration
   */
  getCurrentConfiguration(): SyncConfiguration {
    return this.currentConfig || { ...DEFAULT_SYNC_CONFIG };
  }

  /**
   * Update sync enabled status
   */
  async setSyncEnabled(enabled: boolean): Promise<void> {
    const config = this.getCurrentConfiguration();
    config.enabled = enabled;
    await this.saveConfiguration(config);
  }

  /**
   * Update auto-sync settings
   */
  async updateAutoSyncSettings(autoSync: SyncConfiguration['autoSync']): Promise<void> {
    const config = this.getCurrentConfiguration();
    config.autoSync = { ...autoSync };
    await this.saveConfiguration(config);
  }

}

// Export singleton instance
export const syncConfigurationService = new SyncConfigurationService();
